import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import RestoreFromTrashRoundedIcon from '@mui/icons-material/RestoreFromTrashRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import type { ChatAttachment, Message } from '../../types/chat'
import type { EntityReference } from '../../types/entity'
import { useNls } from '../../nls/nlsProvider'
import { SyncStatusIndicator } from './SyncStatusIndicator'
import { inferAttachmentPreviewKind } from '../Common/attachmentPreviewHelpers'
import { StatusChip } from '../Common'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { getChatRoleLabel } from './chatShellChrome'
import { AnalyticsCardBlock } from './AnalyticsCardBlock'
import { ChartCardBlock } from './ChartCardBlock'
import { useState, type KeyboardEvent, type MouseEvent } from 'react'
import { DeliveryTicks } from '../Common/DeliveryTicks'
import { retryWhatsappMessage } from '../../services/whatsappService'
import { WhatsappCommandCard } from './WhatsappCommandCard'
import { FieldPassSummaryCard } from './FieldPassSummaryCard'

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const formatTimestamp = (timestamp: unknown): string => {
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    try {
      return (timestamp as { toDate: () => Date }).toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    } catch {
      return ''
    }
  }
  return ''
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const attachmentKind = (attachment: ChatAttachment): 'audio' | 'video' | 'image' | 'pdf' | 'file' => {
  const inferred = inferAttachmentPreviewKind({ name: attachment.name, contentType: attachment.contentType })
  if (inferred === 'audio' || inferred === 'video' || inferred === 'image') return inferred
  if (attachment.contentType.toLowerCase() === 'application/pdf' || attachment.name.toLowerCase().endsWith('.pdf')) return 'pdf'
  return 'file'
}

const unavailableAttachmentText = (attachment: ChatAttachment, fallback: string) => attachment.unavailableReason || fallback

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Workspace items now use `/Title` in composer drafts (`#` is reserved for
// user-typed tags). Older messages authored before this change still contain
// `#Title` — buildInlineParts probes both sigils so old bubbles keep rendering
// their entity chips inline.
const getReferenceToken = (reference: EntityReference): string => {
  const title = typeof reference.snapshot.title === 'string' && reference.snapshot.title.trim()
    ? reference.snapshot.title.trim()
    : reference.entityId
  return `${reference.entityType === 'USER' ? '@' : '/'}${title}`
}

const getLegacyReferenceToken = (reference: EntityReference): string | null => {
  if (reference.entityType === 'USER') return null
  const title = typeof reference.snapshot.title === 'string' && reference.snapshot.title.trim()
    ? reference.snapshot.title.trim()
    : reference.entityId
  return `#${title}`
}

const buildInlineParts = (message: Message) => {
  const text = message.textContent || ''
  const references = message.entityReferences ?? []
  const matches: Array<{ start: number; end: number; reference?: EntityReference; tag?: string }> = []

  for (const reference of references) {
    const token = getReferenceToken(reference)
    let match = new RegExp(escapeRegExp(token), 'i').exec(text)
    if (!match) {
      const legacy = getLegacyReferenceToken(reference)
      if (legacy) match = new RegExp(escapeRegExp(legacy), 'i').exec(text)
    }
    if (match) matches.push({ start: match.index, end: match.index + match[0].length, reference })
  }

  for (const tag of message.tags ?? []) {
    const match = new RegExp(`(^|\\s)(#${escapeRegExp(tag)})(?=$|\\s|[.,!?;:])`, 'i').exec(text)
    if (match) {
      const start = match.index + match[1].length
      matches.push({ start, end: start + match[2].length, tag })
    }
  }

  matches.sort((a, b) => a.start - b.start || b.end - a.end)
  const parts: Array<
    | { kind: 'text'; text: string }
    | { kind: 'reference'; text: string; reference: EntityReference }
    | { kind: 'tag'; text: string; tag: string }
  > = []
  const usedReferenceKeys = new Set<string>()
  let cursor = 0

  for (const match of matches) {
    if (match.start < cursor) continue
    if (match.start > cursor) parts.push({ kind: 'text', text: text.slice(cursor, match.start) })
    const segmentText = text.slice(match.start, match.end)
    if (match.reference) {
      usedReferenceKeys.add(`${match.reference.entityType}:${match.reference.entityId}`)
      parts.push({ kind: 'reference', text: segmentText, reference: match.reference })
    } else if (match.tag) {
      parts.push({ kind: 'tag', text: segmentText, tag: match.tag })
    }
    cursor = match.end
  }

  if (cursor < text.length) parts.push({ kind: 'text', text: text.slice(cursor) })

  for (const reference of references) {
    const key = `${reference.entityType}:${reference.entityId}`
    if (!usedReferenceKeys.has(key)) {
      if (parts.length > 0) parts.push({ kind: 'text', text: ' ' })
      parts.push({ kind: 'reference', text: getReferenceToken(reference), reference })
    }
  }

  return parts
}

type MessageBubbleProps = {
  message: Message
  isSelf: boolean
  selectable?: boolean
  selected?: boolean
  onToggleSelected?: () => void
  onTagClick?: (tag: string) => void
  onEntityClick?: (reference: EntityReference) => void
  translation?: string
  translating?: boolean
  onTranslate?: () => void
  onRetry?: () => void
  onPreviewAttachment?: (attachment: ChatAttachment) => void
  canEdit?: boolean
  canDelete?: boolean
  canRestore?: boolean
  onEdit?: () => void
  onDelete?: () => void
  onRestore?: () => void
  /**
   * Optional preview of the parent message when this message is a reply
   * (i.e. `message.replyToId` is set). Rendered as an inline quote block at
   * the top of the bubble. Callers look up the parent in their own store —
   * `MessageBubble` stays decoupled from ChatContext so it can be used from
   * tests and preview surfaces.
   */
  replyToPreview?: {
    authorName: string
    text: string
    isDeleted?: boolean
  } | null
  /** Fires when the user taps the reply-quote chip to jump to the parent. */
  onJumpToReplyParent?: () => void
  /** Fires when the user taps the reply action for this message. */
  onReply?: () => void
  workspaceId?: string
  channelId?: string
  canRetryWhatsapp?: boolean
  onRetryWhatsapp?: () => Promise<void> | void
}

const summarizeReplyPreview = (text: string, cap = 90): string => {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim()
  if (!trimmed) return ''
  return trimmed.length > cap ? `${trimmed.slice(0, cap - 1)}…` : trimmed
}

export const MessageBubble = ({
  message,
  isSelf,
  selectable,
  selected,
  onToggleSelected,
  onTagClick,
  onEntityClick,
  translation,
  translating,
  onTranslate,
  onRetry,
  onPreviewAttachment,
  canEdit,
  canDelete,
  canRestore,
  onEdit,
  onDelete,
  onRestore,
  replyToPreview,
  onJumpToReplyParent,
  onReply,
  workspaceId,
  channelId,
  canRetryWhatsapp,
  onRetryWhatsapp,
}: MessageBubbleProps) => {
  const { t } = useNls()
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)

  const waMeta = message.metadata?.whatsapp
  const isOutbound = waMeta?.direction === 'OUT'
  const isPausedOrFailed = waMeta?.status === 'PAUSED' || waMeta?.status === 'FAILED'
  const inRoles = canRetryWhatsapp !== undefined ? canRetryWhatsapp : true
  const showRetry = isOutbound && isPausedOrFailed && inRoles

  const handleRetryWhatsapp = async () => {
    if (retrying) return
    setRetrying(true)
    setRetryError(null)
    try {
      if (onRetryWhatsapp) {
        await onRetryWhatsapp()
      } else if (workspaceId && channelId) {
        await retryWhatsappMessage({
          workspaceId,
          channelId,
          messageId: message.id,
        })
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Refused to retry WhatsApp message'
      setRetryError(msg)
    } finally {
      setRetrying(false)
    }
  }
  const inlineParts = buildInlineParts(message)
  const isDeleted = message.isDeleted === true
  const toggleFromBubble = (event: MouseEvent<HTMLElement>) => {
    if (!selectable || !onToggleSelected) return
    const target = event.target as HTMLElement
    if (target.closest('button,a,input,textarea,select')) return
    onToggleSelected()
  }
  const handleBubbleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!selectable || !onToggleSelected) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onToggleSelected()
  }
  return (
    <div className={`chatMessageRow ${isSelf ? 'isSelf' : ''}`}>
      {!isSelf ? (
        message.authorAvatarUrl ? (
          <img src={message.authorAvatarUrl} alt="" className="chatMessageAvatar" />
        ) : (
          <span className="chatMessageAvatar chatMessageAvatarFallback">{initialsOf(message.authorName)}</span>
        )
      ) : null}
      <article
        className={`chatMessageBubble ${isSelf ? 'isSelf' : 'isOther'}${selectable ? ' isAiSelectable' : ''}${selected ? ' isAiSelected' : ''}`}
        onClick={toggleFromBubble}
        onKeyDown={handleBubbleKeyDown}
        tabIndex={selectable ? 0 : undefined}
        role={selectable ? 'button' : undefined}
        aria-pressed={selectable ? Boolean(selected) : undefined}
        aria-label={selectable ? t('NLSChatAriaSelectChatForAi') : undefined}
      >
        {selectable && selected ? (
          <span className="chatMessageAiSelectedMark" aria-hidden="true">
            <CheckRoundedIcon fontSize="inherit" />
          </span>
        ) : null}
        <strong className="chatMessageAuthor">
          <span>{isSelf ? t('NLSChatLabelYou') : message.authorName}</span>
          <small>{getChatRoleLabel(t, message.authorRole)}</small>
        </strong>
        {waMeta?.via === 'PHONE' && (
          <StatusChip tone="neutral" size="sm" className="chatMessageWhatsappTag" data-testid="whatsapp-phone-tag">
            {t('NLSChatTagViaPhone')}
          </StatusChip>
        )}
        {waMeta?.imported === true && (
          <StatusChip tone="neutral" size="sm" className="chatMessageWhatsappTag" data-testid="whatsapp-imported-tag">
            {t('NLSChatTagImported')}
          </StatusChip>
        )}
        {(message.source === 'WHATSAPP' || waMeta?.direction === 'IN') && (
          <StatusChip tone="success" size="sm" className="chatMessageInboundMarker" data-testid="whatsapp-inbound-marker">
            WhatsApp
          </StatusChip>
        )}
        {replyToPreview ? (
          <Pressable
            className={`chatMessageReplyQuote ${onJumpToReplyParent ? 'isInteractive' : ''}`}
            onClick={onJumpToReplyParent}
            disabled={!onJumpToReplyParent}
            aria-label={t('NLSChatAriaReplyingTo', { author: replyToPreview.authorName })}
          >
            <span className="chatMessageReplyQuote__author">{replyToPreview.authorName}</span>
            <span className="chatMessageReplyQuote__preview">
              {replyToPreview.isDeleted
                ? t('NLSChatMessageDeleted')
                : summarizeReplyPreview(replyToPreview.text) || t('NLSChatAttachmentGeneric')}
            </span>
          </Pressable>
        ) : null}
        {isDeleted ? (
          <p className="chatMessageText chatMessageText--deleted">
            {t('NLSChatMessageDeleted')}
          </p>
        ) : inlineParts.length > 0 ? (
          <p className="chatMessageText">
            {inlineParts.map((part, index) => {
              if (part.kind === 'reference') {
                return (
                  <Pressable
                    key={`${part.reference.entityType}:${part.reference.entityId}:${index}`}
                    className={`chatInlineToken chatInlineToken--${part.reference.entityType.toLowerCase()}`}
                    onClick={() => onEntityClick?.(part.reference)}
                  >
                    {part.text}
                  </Pressable>
                )
              }
              if (part.kind === 'tag') {
                if (selectable && onTagClick) {
                  return (
                    <Pressable
                      key={`${part.tag}:${index}`}
                      className="chatInlineToken chatInlineToken--tag"
                      onClick={() => onTagClick(part.tag)}
                    >
                      {part.text}
                    </Pressable>
                  )
                }
                return <span key={`${part.tag}:${index}`} className="chatInlineToken chatInlineToken--tag">{part.text}</span>
              }
              return <span key={`text:${index}`}>{part.text}</span>
            })}
          </p>
        ) : null}
        {!isDeleted ? <AnalyticsCardBlock card={message.metadata?.analyticsCard} /> : null}
        {!isDeleted && message.metadata?.chartCard ? <ChartCardBlock card={message.metadata.chartCard} /> : null}
        {!isDeleted && message.metadata?.fieldPassSummary ? <FieldPassSummaryCard summary={message.metadata.fieldPassSummary} /> : null}
        {translation && !isDeleted ? <div className="chatTranslationText">{translation}</div> : null}
        {!isDeleted && message.attachments && message.attachments.length > 0 ? (
          <div className="chatAttachmentList">
            {message.attachments.map((attachment) => {
              if (attachment.isUnavailable) {
                return (
                  <Pressable
                    key={attachment.id}
                    onClick={() => onPreviewAttachment?.(attachment)}
                    className="teamChatMessageAttachment"
                  >
                    <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                    <span>{attachment.name}</span>
                    <span>{unavailableAttachmentText(attachment, t('NLSChatTextAttachmentUnavailable'))}</span>
                  </Pressable>
                )
              }
              const kind = attachmentKind(attachment)
              if (kind === 'audio') {
                return (
                  <figure key={attachment.id} className="chatAttachmentPreview chatAttachmentPreview--audio">
                    <figcaption>
                      <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                      <Button size="sm" variant="link" onClick={() => onPreviewAttachment?.(attachment)}>{attachment.name}</Button>
                      <span>{formatFileSize(attachment.size)}</span>
                    </figcaption>
                    <audio controls preload="metadata" src={attachment.downloadUrl} />
                  </figure>
                )
              }
              if (kind === 'video') {
                return (
                  <figure key={attachment.id} className="chatAttachmentPreview chatAttachmentPreview--video">
                    <figcaption>
                      <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                      <Button size="sm" variant="link" onClick={() => onPreviewAttachment?.(attachment)}>{attachment.name}</Button>
                      <span>{formatFileSize(attachment.size)}</span>
                    </figcaption>
                    <video controls preload="metadata" src={attachment.downloadUrl} />
                  </figure>
                )
              }
              if (kind === 'image') {
                return (
                  <figure key={attachment.id} className="chatAttachmentPreview chatAttachmentPreview--image">
                    <Pressable className="chatAttachmentPreviewMediaButton" onClick={() => onPreviewAttachment?.(attachment)}>
                      <img src={attachment.downloadUrl} alt={attachment.name} loading="lazy" />
                    </Pressable>
                    <figcaption>
                      <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                      <Button size="sm" variant="link" onClick={() => onPreviewAttachment?.(attachment)}>{attachment.name}</Button>
                      <span>{formatFileSize(attachment.size)}</span>
                    </figcaption>
                  </figure>
                )
              }
              if (kind === 'pdf') {
                return (
                  <figure key={attachment.id} className="chatAttachmentPreview chatAttachmentPreview--pdf">
                    <figcaption>
                      <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                      <Button size="sm" variant="link" onClick={() => onPreviewAttachment?.(attachment)}>{attachment.name}</Button>
                      <span>{formatFileSize(attachment.size)}</span>
                    </figcaption>
                    <iframe src={attachment.downloadUrl} title={attachment.name} loading="lazy" />
                  </figure>
                )
              }
              return (
                <Pressable key={attachment.id} onClick={() => onPreviewAttachment?.(attachment)} className="teamChatMessageAttachment">
                  <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                  <span>{attachment.name}</span>
                  <span>{formatFileSize(attachment.size)}</span>
                </Pressable>
              )
            })}
          </div>
        ) : null}
        {waMeta?.command ? (
          <WhatsappCommandCard
            command={waMeta.command}
            workspaceId={workspaceId}
            channelId={channelId}
            messageId={message.id}
          />
        ) : null}
        <footer className="chatMessageFooter">
          <span>{formatTimestamp(message.timestamp)}</span>
          {isOutbound && (
            <DeliveryTicks status={waMeta?.status} testId="whatsapp-delivery-ticks" />
          )}
          {isOutbound && waMeta?.status === 'FAILED' && (waMeta.failureTitle || waMeta.error) ? (
            <span className="chatMessageFailureText" data-testid="whatsapp-failure-text">
              {waMeta.failureTitle || waMeta.error}
            </span>
          ) : null}
          {showRetry ? (
            <Button
              size="sm"
              variant="tertiary"
              onClick={handleRetryWhatsapp}
              disabled={retrying}
              loading={retrying}
              data-testid="whatsapp-retry-button"
            >
              {t('NLSChatActionRetry')}
            </Button>
          ) : null}
          {retryError ? (
            <span className="chatMessageRetryError" data-testid="whatsapp-retry-error">
              {retryError}
            </span>
          ) : null}
          {!isDeleted && (message.aiSessionRefs?.length ?? 0) > 0 ? (
            <span title={`${message.aiSessionRefs?.length ?? 0} AI Magic session${message.aiSessionRefs?.length === 1 ? '' : 's'}`}>
              <StatusChip
                size="sm"
                tone="success"
                icon={<NorthEastRoundedIcon fontSize="inherit" />}
                className="chatMessageAiBadge"
              >
                AI
              </StatusChip>
            </span>
          ) : null}
          {!isDeleted && !isSelf && onTranslate ? (
            <IconButton
              size="sm"
              variant="tertiary"
              icon={<TranslateRoundedIcon fontSize="small" />}
              label={translating ? t('NLSChatActionTranslating') : t('NLSChatActionTranslate')}
              onClick={onTranslate}
              disabled={translating}
              loading={translating}
            />
          ) : null}
          {!isDeleted && onReply ? (
            <IconButton
              size="sm"
              variant="tertiary"
              icon={<ReplyRoundedIcon fontSize="small" />}
              label={t('NLSChatActionReply')}
              onClick={onReply}
            />
          ) : null}
          {!isDeleted && message.editedAt ? <span>{t('NLSChatLabelEdited')}</span> : null}
          {!isDeleted && canEdit ? (
            <IconButton
              size="sm"
              variant="tertiary"
              icon={<EditRoundedIcon fontSize="small" />}
              label={t('NLSChatActionEdit')}
              onClick={onEdit}
            />
          ) : null}
          {!isDeleted && canDelete ? (
            <IconButton
              size="sm"
              variant="tertiary"
              tone="danger"
              icon={<DeleteOutlineRoundedIcon fontSize="small" />}
              label={t('NLSChatActionDelete')}
              onClick={onDelete}
            />
          ) : null}
          {isDeleted && canRestore ? (
            <IconButton
              size="sm"
              variant="tertiary"
              icon={<RestoreFromTrashRoundedIcon fontSize="small" />}
              label={t('NLSChatActionUndoDelete')}
              onClick={onRestore}
            />
          ) : null}
          <SyncStatusIndicator status={message.optimisticStatus} onRetry={onRetry} />
        </footer>
      </article>
    </div>
  )
}
